// Apple Pay inside the iOS app. The web wallets cannot run in a Capacitor web
// view (Apple disables Apple Pay JS there — see InlinePayOptions), so the app
// presents Apple's own sheet through the Stripe iOS SDK and confirms the SAME
// PaymentIntent the server minted for the web flow. Nothing changes server-
// side: the intent is created with automatic payment methods + manual
// capture, and /confirm-intent is called exactly as after a web wallet pay.
//
// Prerequisites outside this repo (Apple Developer + Stripe Dashboard):
//   1. Merchant ID `merchant.com.requesta.app` on the team, listed under
//      "Apple Pay Payment Processing" for App ID com.requesta.app
//      (the entitlement in ios/App/App/App*.entitlements names it).
//   2. An Apple Pay payment-processing certificate for that Merchant ID,
//      generated from Stripe (Settings → Payment methods → Apple Pay → iOS
//      certificates) and uploaded back to Stripe. Without it the sheet
//      opens but the payment fails.
//
// Plugin objects are Proxies — never `await` or `return` the plugin itself
// (reference_capacitor_plugin_proxy_await); only its method calls.
import { Capacitor } from "@capacitor/core";
import { Stripe, ApplePayEventsEnum } from "@capacitor-community/stripe";
import { STRIPE_PK } from "./fanCheckout";

export const APPLE_PAY_MERCHANT_ID = "merchant.com.requesta.app";
/** Requesta LLC's country — the merchant country Apple Pay bills under. */
export const APPLE_PAY_MERCHANT_COUNTRY = "US";

export type NativeApplePayResult = "success" | "canceled" | "failed";

const isIosApp = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

let readyPromise: Promise<boolean> | null = null;
/** Stripe.initialize once per app run; false where native Apple Pay cannot exist. */
function ready(): Promise<boolean> {
  if (!readyPromise) {
    readyPromise = (async () => {
      if (!isIosApp() || !STRIPE_PK) return false;
      try {
        await Stripe.initialize({ publishableKey: STRIPE_PK });
        return true;
      } catch (e) {
        console.error("native Stripe init failed:", e);
        return false;
      }
    })();
  }
  return readyPromise;
}

let availablePromise: Promise<boolean> | null = null;
/** True on an iPhone/iPad inside the app that can present Apple Pay. */
export function nativeApplePayAvailable(): Promise<boolean> {
  if (!availablePromise) {
    availablePromise = (async () => {
      if (!(await ready())) return false;
      try {
        await Stripe.isApplePayAvailable(); // rejects where the device cannot
        return true;
      } catch {
        return false;
      }
    })();
  }
  return availablePromise;
}

/**
 * Present the Apple Pay sheet for an existing PaymentIntent. `amount` is in
 * the currency's major unit (10.85 → $10.85), exactly what the fan sees on
 * the button. Resolves once the sheet is gone; never throws.
 */
export async function payWithNativeApplePay(opts: {
  clientSecret: string;
  amount: number;
  currency: string;
  label?: string;
}): Promise<NativeApplePayResult> {
  if (!(await ready())) return "failed";
  try {
    await Stripe.createApplePay({
      paymentIntentClientSecret: opts.clientSecret,
      paymentSummaryItems: [{ label: opts.label || "Request App", amount: opts.amount }],
      merchantIdentifier: APPLE_PAY_MERCHANT_ID,
      countryCode: APPLE_PAY_MERCHANT_COUNTRY,
      currency: opts.currency.toUpperCase(),
    });
    const { paymentResult } = await Stripe.presentApplePay();
    if (paymentResult === ApplePayEventsEnum.Completed) return "success";
    if (paymentResult === ApplePayEventsEnum.Canceled) return "canceled";
    return "failed";
  } catch (e) {
    console.error("native Apple Pay failed:", e);
    return "failed";
  }
}

/** The PaymentIntent id a client secret belongs to (pi_…_secret_… → pi_…). */
export const intentIdFromSecret = (clientSecret: string): string | undefined =>
  clientSecret.split("_secret")[0] || undefined;
